import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import backendApi from "../api/backendApi";
import LoadingButton from "../components/LoadingButton";

const OtpVerifyPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation() || {};
  const email = state?.email || "";
  const initialResetId = state?.resetId || null;
  const initialExpiresInSec = state?.expiresInSec ?? 600;

  const [resetId, setResetId] = useState(initialResetId);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputsRef = useRef([]);
  const [timeLeft, setTimeLeft] = useState(initialExpiresInSec);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState("");

  // OTP expiry countdown
  useEffect(() => {
    if (!timeLeft) return;
    const t = setInterval(() => setTimeLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(
      () => setResendCooldown((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(t);
  }, [resendCooldown]);

  const formattedTime = useMemo(() => {
    const m = String(Math.floor(timeLeft / 60)).padStart(2, "0");
    const s = String(timeLeft % 60).padStart(2, "0");
    return `${m}:${s}`;
  }, [timeLeft]);

  const handleChange = (index, val) => {
    if (!/^\d?$/.test(val)) return; // digits only
    const next = [...otp];
    next[index] = val;
    setOtp(next);
    if (val && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const code = otp.join("");
  const isComplete = code.length === 6;

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!isComplete || !resetId) return;

    try {
      setSubmitting(true);
      setError("");
      const res = await backendApi.post("/auth/verify-otp", {
        resetId,
        otp: code,
      });
      const { resetToken } = res.data || {};
      navigate("/reset-password", {
        state: { email, resetToken },
        replace: true,
      });
    } catch {
      setError("Invalid or expired OTP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending || resendCooldown > 0) return;
    try {
      setResending(true);
      setError("");
      const res = await backendApi.post("/auth/forgot-password", { email });
      const { resetId: newResetId, expiresInSec } = res.data || {};
      if (newResetId) setResetId(newResetId);
      if (expiresInSec != null) setTimeLeft(expiresInSec);

      setOtp(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();

      setResendCooldown(60);
    } catch {
      setError("Unable to resend OTP. Please try again later.");
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (!email) navigate("/forgot-password", { replace: true });
  }, [email, navigate]);

  const canResend = !resending && resendCooldown === 0;

  return (
    <div className="flex flex-col h-full justify-center items-center">
      <div className="md:max-w-lg w-[calc(100vw-2rem)] p-6 bg-white shadow rounded-3xl">
        <h2 className="text-2xl font-bold mb-1">Enter OTP</h2>
        <p className="text-sm text-gray-600 mb-4">
          We sent a 6-digit code to <span className="font-medium">{email}</span>
        </p>

        {error && <div className="text-red-600 text-sm mb-3">{error}</div>}

        <form onSubmit={handleVerify} className="space-y-5">
          <div className="flex gap-2 justify-between">
            {otp.map((v, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                className="w-12 h-12 border rounded-lg text-center text-xl"
                inputMode="numeric"
                maxLength={1}
                value={v}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !otp[i] && i > 0) {
                    inputsRef.current[i - 1]?.focus();
                  }
                }}
                autoFocus={i === 0}
              />
            ))}
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>
              Expires in: <span className="font-mono">{formattedTime}</span>
            </div>
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className="underline disabled:opacity-60"
            >
              {resending
                ? "Resending..."
                : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Resend OTP"}
            </button>
          </div>

          <LoadingButton
            variant="dark"
            type="submit"
            loading={submitting}
            disabled={!isComplete}
          >
            Verify
          </LoadingButton>
        </form>
      </div>
    </div>
  );
};

export default OtpVerifyPage;
